import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Database, RefreshCw, Loader2, Check, X, AlertCircle, Play,
  Settings, Link2, FileCheck, AlertTriangle, ChevronRight, Zap,
  Clock, ArrowRightLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusColor = (status) => {
  const colors = {
    'connected': 'bg-emerald-100 text-emerald-700',
    'syncing': 'bg-blue-100 text-blue-700',
    'error': 'bg-red-100 text-red-700',
    'pending': 'bg-amber-100 text-amber-700',
    'disconnected': 'bg-gray-100 text-gray-700'
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
};

export default function EHRIntegration() {
  const [activeTab, setActiveTab] = useState('status');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [providers, setProviders] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [configuring, setConfiguring] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [mapping, setMapping] = useState(null);

  const fetchStatus = async () => {
    const token = localStorage.getItem('qapi-token');
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [statusRes, providersRes, syncRes] = await Promise.all([
        fetch(`${API_URL}/api/ehr/status`, { headers }),
        fetch(`${API_URL}/api/ehr/providers`, { headers }),
        fetch(`${API_URL}/api/ehr/sync-status`, { headers })
      ]);

      if (statusRes.ok) setStatus(await statusRes.json());
      if (providersRes.ok) {
        const data = await providersRes.json();
        setProviders(data.providers || []);
      }
      if (syncRes.ok) setSyncStatus(await syncRes.json());
    } catch (error) {
      console.error('Error fetching EHR status:', error);
    } finally {
      setLoading(false);
    }
  };

  const configureProvider = async (provider) => {
    setConfiguring(true);
    const token = localStorage.getItem('qapi-token');

    try {
      const res = await fetch(`${API_URL}/api/ehr/configure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider: provider.id,
          sync_frequency: 'hourly',
          data_types: ['patients', 'census', 'visits', 'staff']
        })
      });

      if (res.ok) {
        await fetchStatus();
        setSelectedProvider(provider);
      }
    } catch (error) {
      console.error('Error configuring provider:', error);
    } finally {
      setConfiguring(false);
    }
  };

  const testConnection = async (providerId) => {
    setTesting(true);
    const token = localStorage.getItem('qapi-token');

    try {
      const res = await fetch(`${API_URL}/api/ehr/test-connection/${providerId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchStatus();
      }
    } catch (error) {
      console.error('Error testing connection:', error);
    } finally {
      setTesting(false);
    }
  };

  const triggerSync = async (providerId) => {
    setSyncing(true);
    const token = localStorage.getItem('qapi-token');

    try {
      const res = await fetch(`${API_URL}/api/ehr/sync/${providerId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchStatus();
      }
    } catch (error) {
      console.error('Error triggering sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  const fetchMapping = async (providerId) => {
    const token = localStorage.getItem('qapi-token');
    try {
      const res = await fetch(`${API_URL}/api/ehr/mapping/${providerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMapping(await res.json());
      }
    } catch (error) {
      console.error('Error fetching mapping:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchStatus(), 100);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading EHR integration status...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="ehr-integration-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-7 w-7 text-blue-500" />
            EHR Integration
          </h1>
          <p className="text-muted-foreground">Bi-directional sync with your Electronic Health Record system</p>
        </div>
        <Button onClick={fetchStatus} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Framework Status</p>
                <p className="text-lg font-semibold text-emerald-600">
                  {status?.framework_ready ? 'Ready' : 'Not Ready'}
                </p>
              </div>
              {status?.framework_ready ? (
                <Check className="h-8 w-8 text-emerald-500" />
              ) : (
                <X className="h-8 w-8 text-red-500" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Integrations</p>
                <p className="text-lg font-semibold">
                  {status?.connected_count || 0} / {status?.total_configured || 0}
                </p>
              </div>
              <Link2 className="h-8 w-8 text-blue-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Connected</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Supported Providers</p>
                <p className="text-lg font-semibold">{providers.length}</p>
              </div>
              <FileCheck className="h-8 w-8 text-violet-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Data Types</p>
                <p className="text-lg font-semibold">{status?.supported_data_types?.length || 0}</p>
              </div>
              <ArrowRightLeft className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="status">Connection Status</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="mapping">Data Mapping</TabsTrigger>
          <TabsTrigger value="sync">Sync History</TabsTrigger>
        </TabsList>

        <TabsContent value="status" className="mt-4">
          {status?.status === 'not_configured' ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  No Integrations Configured
                </CardTitle>
                <CardDescription>
                  Connect your EHR system to enable automatic data synchronization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Setup Steps</h4>
                    <ol className="space-y-2">
                      {status?.setup_steps?.map((step, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <span className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium">
                            {idx + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>

                  <Button onClick={() => setActiveTab('providers')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Configure Integration
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {status?.integrations?.map((integration, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Database className="h-8 w-8 text-blue-500" />
                        <div>
                          <h4 className="font-semibold capitalize">{integration.provider}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={getStatusColor(integration.status)}>
                              {integration.status}
                            </Badge>
                            {integration.enabled && (
                              <Badge variant="outline" className="text-emerald-600">
                                Auto-sync enabled
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testConnection(integration.provider)}
                          disabled={testing}
                        >
                          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => triggerSync(integration.provider)}
                          disabled={syncing || integration.status !== 'connected'}
                        >
                          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-1" /> Sync</>}
                        </Button>
                      </div>
                    </div>

                    {integration.last_sync && (
                      <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Last Sync</p>
                          <p className="font-medium">{new Date(integration.last_sync).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Records Synced</p>
                          <p className="font-medium">{integration.records_synced || 0}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Status</p>
                          <p className={`font-medium ${integration.status === 'connected' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {integration.status === 'connected' ? 'Healthy' : 'Needs attention'}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="providers" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map((provider, idx) => (
              <Card key={idx} className="hover:border-blue-300 transition-colors">
                <CardHeader>
                  <CardTitle className="text-lg">{provider.name}</CardTitle>
                  <CardDescription>{provider.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Integration Type</span>
                      <Badge variant="outline">{provider.integration_type}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <Badge className="bg-emerald-100 text-emerald-700">{provider.status}</Badge>
                    </div>
                    <Button
                      className="w-full mt-2"
                      onClick={() => configureProvider(provider)}
                      disabled={configuring}
                    >
                      {configuring ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Settings className="h-4 w-4 mr-2" />
                          Configure
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Data Types */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Supported Data Types</CardTitle>
              <CardDescription>Data that can be synced between CareLens and your EHR</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {status?.supported_data_types?.map((type, idx) => (
                  <Badge key={idx} variant="outline" className="capitalize py-1 px-3">
                    {type}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mapping" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Field Mapping Configuration</CardTitle>
              <CardDescription>
                How CareLens fields map to your EHR fields
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-2">
                  {['wellsky', 'matrixcare', 'homecare_homebase', 'axxess'].map(provider => (
                    <Button
                      key={provider}
                      variant={mapping?.provider === provider ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => fetchMapping(provider)}
                      className="capitalize"
                    >
                      {provider.replace('_', ' ')}
                    </Button>
                  ))}
                </div>

                {mapping && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-3 capitalize">{mapping.provider} Field Mapping</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-3">CareLens Field</th>
                            <th className="text-center p-3">
                              <ArrowRightLeft className="h-4 w-4 mx-auto" />
                            </th>
                            <th className="text-left p-3">EHR Field</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(mapping.effective_mapping || {}).map(([careLensField, ehrField], idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-3 font-mono text-xs">{careLensField}</td>
                              <td className="p-3 text-center">
                                <ChevronRight className="h-4 w-4 mx-auto text-muted-foreground" />
                              </td>
                              <td className="p-3 font-mono text-xs">{ehrField}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!mapping && (
                  <p className="text-center text-muted-foreground py-8">
                    Select a provider to view field mappings
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sync" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Sync History
              </CardTitle>
              <CardDescription>Recent synchronization activities</CardDescription>
            </CardHeader>
            <CardContent>
              {syncStatus?.sync_details?.length > 0 ? (
                <div className="space-y-4">
                  {syncStatus.sync_details.map((detail, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold capitalize">{detail.provider}</h4>
                        <Badge className={getStatusColor(detail.status)}>{detail.status}</Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Records Synced</p>
                          <p className="font-medium">{detail.records_synced || 0}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Frequency</p>
                          <p className="font-medium capitalize">{detail.sync_frequency}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Last Sync</p>
                          <p className="font-medium">
                            {detail.last_sync ? new Date(detail.last_sync).toLocaleString() : 'Never'}
                          </p>
                        </div>
                      </div>

                      {detail.data_types_enabled?.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm text-muted-foreground mb-2">Data Types Enabled</p>
                          <div className="flex flex-wrap gap-1">
                            {detail.data_types_enabled.map((type, i) => (
                              <Badge key={i} variant="secondary" className="text-xs capitalize">
                                {type}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {detail.recent_syncs?.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm text-muted-foreground mb-2">Recent Activity</p>
                          <div className="space-y-1">
                            {detail.recent_syncs.slice(0, 3).map((sync, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <span>{new Date(sync.timestamp).toLocaleString()}</span>
                                <span className="text-emerald-600">{sync.total_records} records</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Database className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No sync history yet</p>
                  <p className="text-sm text-muted-foreground">Configure an integration to start syncing</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
